import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { DesignConfig, SampleRenderResponse } from '../api/types';

interface Props {
  storeId: string;
  config: DesignConfig;
}

export default function SampleRenderPanel({ storeId, config }: Props) {
  const [data, setData] = useState<SampleRenderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const renderMut = useMutation({
    mutationFn: () => api.sampleRender(storeId, config),
    onSuccess: (r) => {
      setData(r);
      setError(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof HttpError ? e.detail : (e as Error).message;
      setError(msg ?? 'Sample render failed');
      setData(null);
    },
  });

  function copyAll() {
    if (!data) return;
    const text = data.results
      .filter((r) => r.url !== null)
      .map((r) => r.url as string)
      .join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => renderMut.mutate()}
          disabled={renderMut.isPending}
          className="ff-btn-primary text-[12px] py-1.5 px-3"
          data-testid="sample-render-trigger"
        >
          {renderMut.isPending ? 'Rendering…' : 'Render samples'}
        </button>
        {data && (
          <p className="font-mono text-[11px] text-ff-mute">
            {data.results.length} rendered · {data.wall_seconds.toFixed(1)}s
          </p>
        )}
      </div>

      {renderMut.isPending && (
        <p className="font-mono text-[11px] text-ff-mute" data-testid="sample-render-progress">
          Rendering 3 products. Cold cutouts can take ~30s each.
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-3 py-2 text-[12px] text-ff-danger"
          data-testid="sample-render-error"
        >
          {error}
        </div>
      )}

      {data && (
        <div
          className="rounded-2xl border border-ff-forest/30 bg-ff-mint/20 p-3 space-y-3"
          data-testid="sample-render-panel"
        >
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-widish text-ff-forest">
              Sample render
            </p>
            <button
              type="button"
              onClick={copyAll}
              className="text-[11px] font-mono text-ff-forest hover:underline"
              data-testid="sample-render-copy-all"
            >
              {copied ? 'copied!' : 'copy all links'}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {data.results.map((r) => (
              <div
                key={r.product_id}
                className="rounded-lg bg-white shadow-soft overflow-hidden"
                data-testid={`sample-render-card-${r.product_id}`}
              >
                {r.url ? (
                  <img
                    src={r.url}
                    alt={r.title}
                    className="w-full aspect-square object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square bg-ff-danger-soft flex items-center justify-center text-[11px] text-ff-danger text-center px-2">
                    {r.error ?? 'render failed'}
                  </div>
                )}
                <div className="p-2 text-[11px]">
                  <code className="text-ff-mute">{r.product_id}</code>
                  <p className="truncate">{r.title}</p>
                  {r.url && (
                    <div className="mt-1 flex gap-2 font-mono text-[10px]">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-ff-forest hover:underline"
                      >
                        ↗ open
                      </a>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(r.url as string)}
                        className="text-ff-forest hover:underline"
                      >
                        📋 copy
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
